import i18n from "../i18n";
import { monthlyRentTotal } from "./rentPrice";

export const SITE_NAME = "AnyHomes";
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://anyhomes.fr").replace(/\/$/, "");
export const DEFAULT_SOCIAL_IMAGE = "/assets/img/anyhomes-full-logo.png";

export function slugify(text = "") {
  return text
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function buildPropertySlug(id, title = "") {
  const slug = slugify(title) || "annonce-immobiliere";
  return `${slug}-${id}`;
}

/**
 * Chemin SEO interne d'un bien : /annonce/<slug>-<id> (aligné sur le sitemap).
 * Utilisé pour le maillage interne vers les pages biens.
 */
export function propertyPath(property = {}) {
  const id = property?._id || property?.id;
  if (!id) return "/properties";
  const title = property?.propertyTitle || property?.title || "";
  return `/annonce/${buildPropertySlug(id, title)}`;
}

/** Chemin SEO d'une fiche professionnel : /professionnel/<slug>-<id>. */
export function buildProSlug(id, name = "") {
  const slug = slugify(name) || "professionnel-immobilier";
  return `${slug}-${id}`;
}

export function proPath(pro = {}) {
  const id = pro?._id || pro?.id;
  if (!id) return "/prolist";
  const name = pro?.companyName || pro?.pubName || pro?.fullName || "";
  return `/professionnel/${buildProSlug(id, name)}`;
}

export function extractIdFromSlug(segment = "") {
  if (!segment) return null;
  const mongoId = segment.match(/([a-f0-9]{24})$/i);
  if (mongoId) return mongoId[1];
  const parts = segment.split("-");
  const tail = parts[parts.length - 1];
  return /^[a-zA-Z0-9]{3,}$/.test(tail) ? tail : null;
}

export function absoluteUrl(path = "") {
  if (/^https?:\/\//i.test(path)) return path;
  if (!path) return SITE_URL;
  return path.startsWith("/") ? `${siteOrigin()}${path}` : `${siteOrigin()}/${path}`;
}

// Origine « canonique » : le domaine de production (REACT_APP_SITE_URL).
// En pré-rendu local (localhost) on se base sur le domaine de production pour
// que les balises canonical/OG ne pointent jamais vers une URL de build.
export function siteOrigin() {
  if (typeof window === "undefined") return SITE_URL;
  if (/localhost|127\.0\.0\.1/.test(window.location.hostname)) return SITE_URL;
  return window.location.origin;
}

function ensureTag(attr, key, { isLink = false } = {}) {
  let el = document.head.querySelector(`${isLink ? "link" : "meta"}[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement(isLink ? "link" : "meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function setContent(attr, key, value) {
  const el = ensureTag(attr, key);
  el.setAttribute("content", value);
}

export function setCanonical(href) {
  const el = ensureTag("rel", "canonical", { isLink: true });
  el.setAttribute("href", absoluteUrl(href));
}

export function clearHreflang() {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((n) => n.remove());
}

export function setHreflang(url) {
  clearHreflang();
  const target = url
    ? absoluteUrl(url)
    : `${siteOrigin()}${window.location.pathname}${window.location.search}`;
  ["x-default", "fr", "en"].forEach((h) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", h);
    el.setAttribute("href", target);
    document.head.appendChild(el);
  });
}

export function setJsonLd(obj, key) {
  const id = `jsonld-${key}`;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

export function clearJsonLd(key) {
  document.getElementById(`jsonld-${key}`)?.remove();
}

export function currentLocale() {
  const docLang = (document.documentElement.lang || "").toLowerCase().slice(0, 2);
  const i18nLang = (i18n.resolvedLanguage || i18n.language || "").toLowerCase().slice(0, 2);
  const lang = i18nLang || docLang || "fr";
  return lang === "en" ? "en_US" : "fr_FR";
}

/**
 * Applique un jeu complet de balises SEO pour la page courante.
 * Remplace systématiquement title/description/OG/Twitter/canonical + hreflang
 * pour éviter les balises obsolètes lors des navigations SPA.
 * @param {{title?: string; description?: string; image?: string; type?: string; url?: string;}} [opts]
 */
export function applySeo({ title, description, image, type = "website", url } = {}) {
  const finalTitle = title || `${SITE_NAME} — plateforme immobilière`;
  const finalDescription = (description && String(description).trim()) || defaultDescription();
  document.title = finalTitle;

  const ogImage = absoluteUrl(image || DEFAULT_SOCIAL_IMAGE);
  const ogUrl = absoluteUrl(url || (typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/"));
  const locale = currentLocale();

  setContent("name", "description", finalDescription);
  setContent("property", "og:title", finalTitle);
  setContent("property", "og:description", finalDescription);
  setContent("property", "og:image", ogImage);
  setContent("property", "og:type", type);
  setContent("property", "og:url", ogUrl);
  setContent("property", "og:site_name", SITE_NAME);
  setContent("property", "og:locale", locale);
  setContent("name", "twitter:card", "summary_large_image");
  setContent("name", "twitter:title", finalTitle);
  setContent("name", "twitter:description", finalDescription);
  setContent("name", "twitter:image", ogImage);

  setCanonical(url || (typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/"));
  setHreflang(url);
}

export function stripHtmlToText(html = "") {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function safeDescription(text = "", maxLength = 160) {
  const clean = stripHtmlToText(text);
  if (!clean) return defaultDescription();
  return clean.length > maxLength ? `${clean.slice(0, maxLength - 1)}…` : clean;
}

export function defaultDescription() {
  return "AnyHomes — la plateforme immobilière qui simplifie vos projets d'achat, de vente, de location et d'investissement, de A à Z.";
}

/**
 * Construit le schéma RealEstateListing (JSON-LD) d'un bien immobilier.
 */
export function buildPropertyJsonLd({ detail, url, image }) {
  const isSale = detail?.propertyType === "sale";
  return {
    "@context": "https://schema.org/",
    "@type": "RealEstateListing",
    name: detail?.propertyTitle || "Annonce immobilière",
    description: safeDescription(detail?.content, 300),
    url: absoluteUrl(url),
    image: [absoluteUrl(image)],
    datePosted: detail?.createdAt || new Date().toISOString().slice(0, 10),
    offers: {
      "@type": "Offer",
      price: isSale ? detail?.price : monthlyRentTotal(detail),
      priceCurrency: "EUR",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: detail?.city,
      postalCode: detail?.zipcode,
    },
    numberOfRooms: detail?.rooms || detail?.bedrooms,
    numberOfBathroomsTotal: detail?.bathroom,
  };
}

/**
 * Construit le schéma LocalBusiness/RealEstateAgent (JSON-LD) d'une fiche pro.
 */
export function buildProJsonLd({ detail, url, image }) {
  const name = detail?.companyName || detail?.pubName || detail?.fullName || "Professionnel immobilier";
  const role = detail?.proSubFamily || detail?.activity || detail?.proTitle || "";
  const street = detail?.contactStreet || detail?.street || "";
  const postalCode = detail?.contactZipcode || detail?.pinCode || "";
  const city = detail?.contactCity || detail?.city || "";
  const phone = detail?.contactPhone || detail?.companyContactNumber || detail?.mobileNo || "";
  const email = detail?.contactEmail || detail?.companyEmail || "";
  const services = (detail?.servicesYouOffer || []).map((s) => s?.name).filter(Boolean);
  const description =
    safeDescription(detail?.about || detail?.tagline || "", 300) ||
    `${name}${role ? ` — ${role}` : ""}${city ? ` à ${city}` : ""}. Retrouvez ses coordonnées et services sur AnyHomes.`;

  const address = [street, postalCode, city].filter(Boolean).length
    ? {
        "@type": "PostalAddress",
        ...(street ? { streetAddress: street } : {}),
        ...(postalCode ? { postalCode } : {}),
        ...(city ? { addressLocality: city } : {}),
        addressCountry: "FR",
      }
    : undefined;

  return {
    "@context": "https://schema.org/",
    "@type": "RealEstateAgent",
    name,
    description,
    url: absoluteUrl(url),
    image: [absoluteUrl(image || DEFAULT_SOCIAL_IMAGE)],
    ...(phone ? { telephone: phone } : {}),
    ...(email ? { email } : {}),
    ...(address ? { address } : {}),
    ...(services.length ? { knowsAbout: services } : {}),
    ...(city ? { areaServed: city } : {}),
  };
}

/**
 * Construit le schéma Service + Offer (JSON-LD) d'un service à la carte.
 */
export function buildServiceJsonLd({ service, url, price, isFree, providerName }) {
  const title = service?.title_fr || service?.title || "Service immobilier";
  const summary = service?.summary || service?.description || "";
  const city = service?.city || service?.provider?.city || "";
  const amount = Number(price);
  return {
    "@context": "https://schema.org/",
    "@type": "Service",
    name: title,
    description: safeDescription(summary, 300) || title,
    url: absoluteUrl(url),
    ...(providerName ? { provider: { "@type": "RealEstateAgent", name: providerName } } : {}),
    ...(city ? { areaServed: city } : {}),
    offers: {
      "@type": "Offer",
      price: isFree || !Number.isFinite(amount) ? 0 : amount,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: absoluteUrl(url),
    },
  };
}

/**
 * Construit le schéma Demand (JSON-LD) d'une annonce de recherche immobilière.
 */
export function buildSearchListingJsonLd({ listing, url }) {
  const title = listing?.title || "Annonce de recherche immobilière";
  const city = listing?.zones?.[0]?.name || "";
  const postedAt = listing?.publishedAt || listing?.createdAt;
  return {
    "@context": "https://schema.org/",
    "@type": "Demand",
    name: title,
    description: safeDescription(listing?.description || title, 300),
    url: absoluteUrl(url),
    ...(city ? { areaServed: city } : {}),
    ...(postedAt ? { datePosted: new Date(postedAt).toISOString().slice(0, 10) } : {}),
  };
}

/**
 * Construit le schéma Article (JSON-LD) pour les contenus du blog.
 */
export function buildArticleJsonLd({ title, description, image, url, author = "AnyHomes", datePublished, dateModified }) {
  const pubDate = datePublished || new Date().toISOString().slice(0, 10);
  const modDate = dateModified || pubDate;
  return {
    "@context": "https://schema.org/",
    "@type": "Article",
    headline: title,
    description: description || "",
    url: absoluteUrl(url),
    image: image ? [absoluteUrl(image)] : [absoluteUrl(DEFAULT_SOCIAL_IMAGE)],
    datePublished: pubDate,
    dateModified: modDate,
    author: { "@type": "Organization", name: author },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: absoluteUrl(DEFAULT_SOCIAL_IMAGE) },
    },
  };
}